import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ExternalLink, FileText } from 'lucide-react';
import { useAuth } from '@/auth/AuthProvider';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { callRpc, useMutation } from '@/data/rpc';
import { legalLinks } from '@/lib/config';
import { legalRow, type LegalStatus } from './legalStatus';

/**
 * The blocking screen of the Terms gate (change spec terms-acceptance, design 3): the two documents in
 * the UI language (new tab), one checkbox, "I agree" (disabled until ticked) and "Decline and sign out".
 * LegalProvider renders it inside the bare chrome (brand bar, language switch, sign-out, Toaster).
 * It accepts exactly the versions it displayed: when the operator bumped one meanwhile the server
 * answers CWT01, and the gate reads the status again and shows the new version.
 */
export function TermsGateScreen({
  status,
  onAccepted,
  onStale,
}: {
  status: LegalStatus;
  onAccepted: (next: LegalStatus) => void;
  onStale: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { client, signOut } = useAuth();
  const mutation = useMutation();
  const [agreed, setAgreed] = React.useState(false);
  const locale = i18n.resolvedLanguage === 'es' ? 'es' : 'en';
  const links = legalLinks(locale);

  const accept = async () => {
    const res = await mutation.run(() =>
      callRpc<unknown>(client, 'legal_accept', {
        p_terms_version: status.terms_version,
        p_privacy_version: status.privacy_version,
        p_surface: 'portal',
        p_locale: locale,
      }),
    );
    if (res.error) {
      if (res.error.code === 'CWT01') {
        toast.error(t('errors.termsChanged'));
        mutation.clear();
        onStale();
      }
      return;
    }
    const next = legalRow(res.data);
    if (next) onAccepted(next);
    else onStale();
  };

  const documents = [
    { key: 'terms', href: links.terms, label: t('legal.terms'), version: status.terms_version },
    { key: 'privacy', href: links.privacy, label: t('legal.privacy'), version: status.privacy_version },
  ];

  return (
    <Card className="mx-auto max-w-2xl">
      <CardContent className="flex flex-col gap-6 p-6 sm:p-12">
        <div className="flex flex-col items-center gap-4 text-center">
          <span className="grid size-12 place-items-center rounded-full bg-accent-wash">
            <FileText className="size-6 text-accent-base" aria-hidden="true" />
          </span>
          <h1 className="text-2xl font-semibold text-ink">{t('legal.title')}</h1>
          {status.first_terms_accepted_at && <p className="text-base font-medium text-ink">{t('legal.updated')}</p>}
          <p className="text-base text-ink-secondary">{t('legal.body')}</p>
        </div>

        <ul className="flex flex-col gap-2">
          {documents.map((doc) => (
            <li key={doc.key}>
              <a
                href={doc.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-h-touch items-center justify-between gap-3 rounded-md border border-hairline px-4 py-3 hover:bg-accent-wash"
              >
                <span className="flex flex-col">
                  <span className="font-medium text-accent-base underline">{doc.label}</span>
                  {doc.version && (
                    <span className="text-sm text-ink-muted">{t('legal.version', { version: doc.version })}</span>
                  )}
                </span>
                <ExternalLink className="size-4 shrink-0 text-ink-muted" aria-hidden="true" />
                <span className="sr-only">{t('legal.newTab')}</span>
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-start gap-3">
          <Checkbox
            id="legal-agree"
            className="mt-0.5"
            checked={agreed}
            onCheckedChange={(v) => setAgreed(v === true)}
            disabled={mutation.busy}
          />
          <Label htmlFor="legal-agree" className="text-base font-normal leading-snug text-ink">
            {t('legal.agree')}
          </Label>
        </div>

        <p className="text-sm text-ink-muted">{t('legal.record')}</p>

        {mutation.error && (
          <p role="alert" className="text-sm text-danger">
            {mutation.error}
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={() => void signOut()} disabled={mutation.busy}>
            {t('legal.decline')}
          </Button>
          <Button type="button" onClick={() => void accept()} disabled={!agreed || mutation.busy}>
            {mutation.busy ? t('legal.accepting') : t('legal.accept')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
